import type { GetServerSidePropsContext } from 'next';
import type { ParsedUrlQuery } from 'querystring';

import type { OrderFragment } from '@/graphql/saleor/api';
import { NEXT_PUBLIC_DEFAULT_APP_LOCALE } from '@/lib/const';
import { captureError } from '@/lib/errors';
import { getCountryForChannel, getRegionFromLocale } from '@/lib/regions';

import { extractPathFromUrl } from './urlUtils';

const LOCALE_SLUG_PATTERN = /^\/[a-zA-Z]{2}-[a-zA-Z]{2,3}\//;

export const removeLocaleFromUrl = (resolvedUrl: string) => {
  return resolvedUrl?.replace(LOCALE_SLUG_PATTERN, '/');
};

export const redirectToCorrectOrderLocale = (
  context: Partial<GetServerSidePropsContext>,
  locale: string,
  order: OrderFragment,
  resolvedUrl: string,
) => {
  const { country: countryCurrent } = getRegionFromLocale(locale);

  const countryOrder = getCountryForChannel(order.channel.slug);

  if (countryOrder.channelSlug !== countryCurrent.channelSlug) {
    const channelSlug = countryOrder.slug;

    const localeSlug =
      countryOrder.locales.find(
        ({ slug }) => slug === context.req?.headers['accept-language'],
      )?.slug ?? countryOrder.locales[0].slug;

    const resolvedUrlWithoutLocale = removeLocaleFromUrl(resolvedUrl);

    return {
      redirect: {
        destination: `/${channelSlug}-${localeSlug}${resolvedUrlWithoutLocale}`,

        permanent: false,
      },
    };
  }

  return null;
};

export const getLanguageCode = (locale: string) => {
  const languageCode = locale.split('-');
  return languageCode[1];
};

export const getLocaleFromParams = (params?: ParsedUrlQuery) => {
  const lang = Array.isArray(params?.lang) ? params?.lang[0] : params?.lang;
  if (!lang) {
    captureError(new Error('Locale parameter is undefined'));
  }
  return lang || NEXT_PUBLIC_DEFAULT_APP_LOCALE;
};

export const getNormalizedPathname = (pathname: string) => {
  return pathname.replace(/^\/\[lang\]/, ''); // Removes only the "/[lang]" prefix if it exists in the pathname
};

export const hasMultipleLocales = (url: string) => {
  const pathname = extractPathFromUrl(url);
  const pathSegments = pathname.split('/').filter(Boolean);
  const localePattern = /^[a-z]{2}-[a-z]{2,3}$/;
  const matchingLocales = [];
  for (const pathSegment of pathSegments) {
    if (localePattern.test(pathSegment)) {
      matchingLocales.push(pathSegment);
    } else if (matchingLocales.length) {
      break;
    }
  }
  return matchingLocales.length > 1;
};
